import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Loader, Panel } from '@/shared/ui';
import type { Datasource } from '../../model/types';
import { DatasourceIds } from '../../model/useDatasources';
import { DATASOURCE_TYPE_LABEL_MAP } from '../DatasourceCard/DatasourceCard';
import { StatusIndicator } from '../StatusIndicator/StatusIndicator';

interface Props {
  loading: boolean;
  addButtonSlot: ReactNode;
  datasource: Datasource;
  addIntegrationEditorSlot?: ReactNode;
  integrationsSlot: ReactNode[];
}

export function DatasourcePanel({
  addButtonSlot,
  datasource,
  loading,
  addIntegrationEditorSlot,
  integrationsSlot,
}: Props) {
  const isCustom = datasource.id === DatasourceIds.custom;
  const showEmptyState =
    !addIntegrationEditorSlot &&
    (!integrationsSlot || integrationsSlot.length === 0) &&
    !loading;

  return (
    <Panel
      sx={{
        flex: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            alt={`${datasource.name} logo`}
            src={datasource.iconUrl}
            sx={{
              width: 60,
              height: 60,
              borderRadius: 1,
              border: '1px solid #d9d9d9',
              objectFit: 'contain',
              backgroundColor: 'white',
            }}
          />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 500 }}>
                {datasource.name}
              </Typography>
              <StatusIndicator status={datasource.status} />
            </Box>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              {isCustom ? '' : DATASOURCE_TYPE_LABEL_MAP[datasource.type]}
            </Typography>
          </Box>
        </Box>
        {addButtonSlot}
      </Box>
      <Box
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {addIntegrationEditorSlot}
        {integrationsSlot.length > 0 && (
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {integrationsSlot}
            </Box>
          </Box>
        )}
        {showEmptyState && (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <svg
              width="100"
              height="100"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.3335 87.5C8.33314 81.0847 10.1841 74.8056 13.6641 69.4162C17.1441 64.0268 22.1055 59.7561 27.9527 57.1167C33.7999 54.4773 40.2845 53.5813 46.6283 54.5362C52.9722 55.491 58.9057 58.2563 63.7168 62.5M79.1668 66.6667V91.6667M91.6668 79.1667H66.6668M62.5002 33.3333C62.5002 44.8393 53.1728 54.1667 41.6668 54.1667C30.1609 54.1667 20.8335 44.8393 20.8335 33.3333C20.8335 21.8274 30.1609 12.5 41.6668 12.5C53.1728 12.5 62.5002 21.8274 62.5002 33.3333Z"
                stroke="#989CA9"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 500, fontSize: '1.125rem' }}>
                Add Your First Account
              </Typography>
              <Typography color="secondary" sx={{ fontSize: '0.875rem' }}>
                There's nothing here yet. Get started by adding a account
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Loader active={loading} />
    </Panel>
  );
}
